import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import ReactFlow, { Background, ControlButton, Controls, MarkerType, applyNodeChanges } from 'reactflow';
import 'reactflow/dist/style.css';

import { AppTheme } from '../../../../styles/AppTheme';
import { ChatbotDeletableEdge } from '../whatsapp-chatbot/ChatbotDeletableEdge';
import { AutomationActionNode } from './AutomationActionNode';
import { AutomationAddActionMenu } from './AutomationAddActionMenu';
import { AutomationStepNode } from './AutomationStepNode';
import { AutomationTriggerNode } from './AutomationTriggerNode';
import { HANDLE_OUT, NODE_TYPE_ACTION, NODE_TYPE_STOP, NODE_TYPE_TRIGGER, autoLayoutAutomation } from './automationGraph';

const EMPTY_NODES = [];
const EMPTY_EDGES = [];
const nodeTypes = {
  [NODE_TYPE_TRIGGER]: AutomationTriggerNode,
  [NODE_TYPE_ACTION]: AutomationActionNode,
  default: AutomationStepNode,
};
const edgeTypes = {
  deletable: ChatbotDeletableEdge,
};
const edgeMarker = { type: MarkerType.ArrowClosed, color: AppTheme.primaryColor };
const edgeStyle = { stroke: AppTheme.primaryColor, strokeDasharray: '6 4' };
const ADD_OFFSET_Y = 40;

function nodeIdFromElement(el) {
  while (el && el !== document.body) {
    if (el.classList && el.classList.contains('react-flow__node')) {
      return el.getAttribute('data-id');
    }
    el = el.parentElement;
  }
  return null;
}

function nodeIdFromEvent(event) {
  let el = event && event.target;
  if (event && event.changedTouches && event.changedTouches[0]) {
    const touch = event.changedTouches[0];
    el = document.elementFromPoint(touch.clientX, touch.clientY);
  }
  return nodeIdFromElement(el);
}

function nodeIdFromPoint(x, y) {
  if (x == null || y == null || typeof document.elementFromPoint !== 'function') return null;
  return nodeIdFromElement(document.elementFromPoint(x, y));
}

function eventClientPoint(event) {
  if (!event) return null;
  if (event.changedTouches && event.changedTouches[0]) {
    const touch = event.changedTouches[0];
    if (touch.clientX == null || touch.clientY == null) return null;
    return { x: touch.clientX, y: touch.clientY };
  }
  if (event.clientX == null || event.clientY == null) return null;
  return { x: event.clientX, y: event.clientY };
}

function isPointInsideEl(el, point) {
  if (!el || !point) return false;
  const rect = el.getBoundingClientRect();
  return point.x >= rect.left && point.x <= rect.right && point.y >= rect.top && point.y <= rect.bottom;
}

function sourceHandleFree(nodeId, sourceHandle, edges) {
  if (!nodeId) return false;
  const handle = sourceHandle || HANDLE_OUT;
  return !(edges || []).some(edge => {
    if (!edge) return false;
    const source = edge.source || edge.sourceNodeId;
    const edgeHandle = edge.sourceHandle || HANDLE_OUT;
    return source === nodeId && edgeHandle === handle;
  });
}

function canConnect(connection, nodes, edges) {
  if (!connection || !connection.source || !connection.target) return false;
  if (connection.source === connection.target) return false;
  const target = (nodes || []).find(node => node && node.id === connection.target);
  const source = (nodes || []).find(node => node && node.id === connection.source);
  if (!target || target.type === NODE_TYPE_TRIGGER || target.type === NODE_TYPE_STOP) return false;
  if (source && source.type === NODE_TYPE_STOP) return false;
  const handle = connection.sourceHandle || HANDLE_OUT;
  const taken = (edges || []).some(edge => {
    if (!edge) return false;
    const source = edge.source || edge.sourceNodeId;
    const sourceHandle = edge.sourceHandle || HANDLE_OUT;
    return source === connection.source && sourceHandle === handle;
  });
  return !taken;
}

export function AutomationFlowCanvas(props) {
  const sourceNodes = props.nodes || EMPTY_NODES;
  const edges = props.edges || EMPTY_EDGES;
  const onNodeDelete = props.onNodeDelete;
  const onNodeDuplicate = props.onNodeDuplicate;
  const onEdgeDelete = props.onEdgeDelete;
  const onNodeDragStop = props.onNodeDragStop;
  const onNodeClick = props.onNodeClick;
  const onConnect = props.onConnect;
  const onAddConnectedAction = props.onAddConnectedAction;
  const graph = props.graph;
  const onAutoLayout = props.onAutoLayout;
  const didDragRef = useRef(false);
  const didConnectRef = useRef(false);
  const connectingFromRef = useRef(null);
  const wrapperRef = useRef(null);
  const instanceRef = useRef(null);
  const fitPendingRef = useRef(false);
  const flowNodes = useMemo(() => {
    return sourceNodes.map(node => {
      if (!node || node.type === NODE_TYPE_TRIGGER || node.type === NODE_TYPE_STOP) return node;
      return {
        ...node,
        data: {
          ...(node.data || {}),
          onDelete: onNodeDelete ? () => onNodeDelete(node.id) : undefined,
          onDuplicate: onNodeDuplicate ? () => onNodeDuplicate(node.id) : undefined,
        },
      };
    });
  }, [sourceNodes, onNodeDelete, onNodeDuplicate]);
  const flowEdges = useMemo(() => {
    return edges.map(edge => ({
      ...edge,
      type: 'deletable',
      data: { onDelete: onEdgeDelete },
      markerEnd: edgeMarker,
      style: edgeStyle,
    }));
  }, [edges, onEdgeDelete]);
  const [nodes, setNodes] = useState(flowNodes);
  const [addMenu, setAddMenu] = useState(null);
  useEffect(() => {
    setNodes(flowNodes);
  }, [flowNodes]);
  useEffect(() => {
    if (!fitPendingRef.current) return;
    fitPendingRef.current = false;
    const inst = instanceRef.current;
    if (inst) requestAnimationFrame(() => inst.fitView());
  }, [nodes]);
  const handleAutoLayout = useCallback(() => {
    const inst = instanceRef.current;
    if (!inst || !onAutoLayout) return;
    const next = autoLayoutAutomation(graph, inst.getNodes());
    if (next === graph) {
      inst.fitView();
      return;
    }
    fitPendingRef.current = true;
    onAutoLayout(next);
  }, [graph, onAutoLayout]);
  const onNodesChange = useCallback((changes) => {
    const next = [];
    for (let i = 0; i < changes.length; i++) {
      if (changes[i].type !== 'remove') next.push(changes[i]);
    }
    setNodes(current => applyNodeChanges(next, current));
  }, []);
  const handleNodeDragStart = useCallback(() => {
    didDragRef.current = false;
  }, []);
  const handleNodeDrag = useCallback(() => {
    didDragRef.current = true;
  }, []);
  const handleNodeDragStop = useCallback((event, node) => {
    if (!node || !onNodeDragStop) return;
    onNodeDragStop(node.id, node.position.x, node.position.y);
  }, [onNodeDragStop]);
  const handleNodeClick = useCallback((event, node) => {
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }
    if (onNodeClick) onNodeClick(event, node);
  }, [onNodeClick]);
  const handleConnect = useCallback((params) => {
    didConnectRef.current = true;
    if (onConnect) onConnect(params);
  }, [onConnect]);
  const handleConnectStart = useCallback((_, params) => {
    didConnectRef.current = false;
    connectingFromRef.current = params && params.nodeId
      ? { nodeId: params.nodeId, handleType: params.handleType, handleId: params.handleId }
      : null;
  }, []);
  const closeAddMenu = useCallback(() => {
    setAddMenu(null);
  }, []);
  const handleAddMenuSelect = useCallback(() => {
    const menu = addMenu;
    setAddMenu(null);
    if (!menu || !onAddConnectedAction) return;
    const inst = instanceRef.current;
    if (!inst || typeof inst.screenToFlowPosition !== 'function') return;
    const point = inst.screenToFlowPosition({ x: menu.clientX, y: menu.clientY });
    if (!point || point.x == null || point.y == null) return;
    onAddConnectedAction(menu.nodeId, menu.handleId, {
      posX: point.x,
      posY: point.y - ADD_OFFSET_Y,
    });
  }, [addMenu, onAddConnectedAction]);
  const handleConnectEnd = useCallback((event) => {
    const from = connectingFromRef.current;
    connectingFromRef.current = null;
    if (didConnectRef.current || !from || !from.nodeId) return;
    if (from.handleType && from.handleType !== 'source') return;
    const point = eventClientPoint(event);
    const droppedId = (point && nodeIdFromPoint(point.x, point.y)) || nodeIdFromEvent(event);
    if (droppedId) {
      const connection = {
        source: from.nodeId,
        target: droppedId,
        sourceHandle: from.handleId || HANDLE_OUT,
      };
      if (!canConnect(connection, sourceNodes, edges)) return;
      if (onConnect) onConnect(connection);
      return;
    }
    if (!onAddConnectedAction) return;
    if (!point || !isPointInsideEl(wrapperRef.current, point)) return;
    const sourceHandle = from.handleId || HANDLE_OUT;
    if (!sourceHandleFree(from.nodeId, sourceHandle, edges)) return;
    setAddMenu({
      nodeId: from.nodeId,
      handleId: sourceHandle,
      clientX: point.x,
      clientY: point.y,
    });
  }, [edges, onAddConnectedAction, onConnect, sourceNodes]);
  return (
    <div ref={wrapperRef} className={css(Styles.wrapper)}>
      <ReactFlow
        nodes={nodes}
        edges={flowEdges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={onNodesChange}
        onNodeClick={handleNodeClick}
        onNodeDragStart={handleNodeDragStart}
        onNodeDrag={handleNodeDrag}
        onNodeDragStop={handleNodeDragStop}
        onConnect={handleConnect}
        onConnectStart={handleConnectStart}
        onConnectEnd={handleConnectEnd}
        onInit={(instance) => { instanceRef.current = instance; }}
        isValidConnection={(connection) => canConnect(connection, sourceNodes, edges)}
        connectionLineStyle={edgeStyle}
        deleteKeyCode={null}
        proOptions={{ hideAttribution: true }}
      >
        <Background />
        <Controls showInteractive={false} className={css(Styles.controls)}>
          <ControlButton onClick={handleAutoLayout} title='auto layout' aria-label='auto layout'>
            <i className={'material-icons-outlined ' + css(Styles.controlIcon)}>account_tree</i>
          </ControlButton>
        </Controls>
      </ReactFlow>
      <AutomationAddActionMenu
        open={!!addMenu}
        anchorPosition={addMenu ? { top: addMenu.clientY, left: addMenu.clientX } : undefined}
        onClose={closeAddMenu}
        onSelect={handleAddMenuSelect}
      />
    </div>
  );
}

const Styles = StyleSheet.create({
  wrapper: {
    width: '100%',
    height: '100%',
  },
  controls: {
    boxShadow: 'none',
    border: '1px solid ' + AppTheme.borderColor,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: AppTheme.secondaryBg,
    ':nth-child(1n) .react-flow__controls-button': {
      background: AppTheme.secondaryBg,
      borderBottom: '1px solid ' + AppTheme.borderColor,
      width: 18,
      height: 18,
    },
    ':nth-child(1n) .react-flow__controls-button:last-child': {
      borderBottom: 'none',
    },
    ':nth-child(1n) .react-flow__controls-button:hover': {
      background: AppTheme.primaryBg,
    },
  },
  controlIcon: {
    fontSize: 14,
  },
});
